// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.
// @ts-nocheck

import React, { cloneElement, PropsWithChildren, ReactElement } from "react";

import { ButtonProps } from "@/components/ui/button";
import { IconButtonProps } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { namespacedForwardRef } from "@/lib/utils/react/namespaced-forward-ref";

export const Skeleton = namespacedForwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & {
		/**
		 * Whether to animate the skeleton
		 */
		animate?: boolean;
	},
	{
		Title: typeof Title;
		Button: typeof Button;
		IconButton: typeof IconButton;
	}
>(function Skeleton({ className, animate = true, ...props }, ref) {
	return (
		<div
			data-testid="skeleton"
			className={cn(
				"select-none rounded-sm bg-skeleton text-transparent",
				{
					"animate-pulse": animate,
				},
				className
			)}
			ref={ref}
			{...props}
		/>
	);
});

const fontSizeToHeight = {
	"4xs": "h-2",
	"3xs": "h-3",
	"2xs": "h-4",
	xs: "h-5",
	sm: "h-6",
	md: "h-7",
	lg: "h-8",
	xl: "h-9",
	"2xl": "h-10",
	"3xl": "h-11",
	"4xl": "h-13",
	"5xl": "h-15",
};

function Title({
	size,
	className,
	characters,
	...rest
}: {
	size: keyof typeof fontSizeToHeight;
	characters?: number;
} & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<Skeleton
			data-testid="skeleton.title"
			className={cn(
				"max-w-full",
				{
					["w-1/2"]: !characters,
					[fontSizeToHeight[size]]: size,
				},
				className
			)}
			style={{
				width: characters ? `${characters}ch` : undefined,
				minWidth: characters ? `${characters}ch` : undefined,
			}}
			{...rest}
		/>
	);
}
Skeleton.Title = Title;

const buttonSizes = {
	xxs: "h-5",
	xs: "h-6",
	sm: "h-8",
	md: "h-10",
	lg: "h-12",
	xl: "h-16",
} satisfies Record<NonNullable<ButtonProps["size"]>, string>;

function Button({
	size = "md",
	className,
	...rest
}: {
	size?: keyof typeof buttonSizes;
} & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<Skeleton
			data-testid="skeleton.button"
			className={cn(
				"w-[124px]",
				{
					[buttonSizes[size]]: size,
				},
				className
			)}
			{...rest}
		/>
	);
}
Skeleton.Button = Button;

const iconButtonSizes = {
	"2xs": "h-5 w-5 rounded-md text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
	xs: "h-6 w-6 rounded-md text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
	sm: "h-8 w-8 rounded-md text-sm [&_svg]:h-4.5 [&_svg]:w-4.5",
	md: "h-10 w-10 rounded-md text-sm [&_svg]:h-5 [&_svg]:w-5",
	lg: "h-12 w-12 rounded-md text-md [&_svg]:h-5 [&_svg]:w-5",
	xl: "h-16 w-16 rounded-md text-lg [&_svg]:h-6 [&_svg]:w-6",
} satisfies Record<NonNullable<IconButtonProps["size"]>, string>;

function IconButton({
	size = "md",
	className,
	...rest
}: {
	size?: keyof typeof iconButtonSizes;
} & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<Skeleton
			data-testid="skeleton.iconButton"
			className={cn(
				{
					[iconButtonSizes[size]]: size,
				},
				className
			)}
			{...rest}
		/>
	);
}
Skeleton.IconButton = IconButton;
